import type { TaxonomyPayload, OrgProfilePayload } from "../../lib/api";

import "../../components/CompanyManagementMobile.css";
export const STEPS = [
  { t: "Business details", d: "Add your name and organization type." },
  { t: "What you do", d: "Choose your roles and main industry." },
  { t: "Review", d: "Check your details and submit verification." },
];

export const COMPANY_FLOW_VERSION = 4;

export const ROLE_PAGE_SIZE = 8;

export type Form = Required<Omit<OrgProfilePayload, "complete">>;

export const EMPTY: Form = {
  name: "", tagline: "", city: "",
  organizationType: "", subtype: "",
  ownershipClassifications: [], corporateRelationship: "",
  enterpriseClassifications: [], operatingStatus: "",
  profileRepresents: "", businessRoles: [],
  primaryIndustry: "", psicCode: "", secondaryIndustries: [],
  productsServices: [], customerMarkets: [], serviceCoverage: "",
};

export type ProfileIssue = { field: "name" | "organizationType" | "subtype" | "businessRoles" | "primaryIndustry"; step: number; message: string };

export function profileIssue(form: Form, tax: TaxonomyPayload, retainedSubtype: string, step?: number): ProfileIssue | null {
  const issues: ProfileIssue[] = [];
  if (form.name.trim().length < 2) issues.push({ field: "name", step: 0, message: "Enter a business name with at least 2 characters." });
  if (!form.organizationType) issues.push({ field: "organizationType", step: 0, message: "Choose your organization type." });
  const subtypeOptions = tax.organizationTypes.find(type => type.id === form.organizationType)?.subtypeGroups.flatMap(group => group.options) || [];
  if (subtypeOptions.length && !form.subtype) issues.push({ field: "subtype", step: 0, message: "Choose the subtype for your organization." });
  else if (form.subtype && !subtypeOptions.includes(form.subtype) && form.subtype !== retainedSubtype) issues.push({ field: "subtype", step: 0, message: "Choose a current subtype for your organization." });
  if (!form.businessRoles.length) issues.push({ field: "businessRoles", step: 1, message: "Select at least one business role." });
  if (!form.primaryIndustry.trim()) issues.push({ field: "primaryIndustry", step: 1, message: "Enter your primary industry." });
  return issues.find(issue => step === undefined || issue.step === step) || null;
}

// Map legacy five- and six-step drafts into the combined stages without losing saved work.
// See [business.draft-and-submit](../../../../docs/product/businesses.md#business-draft-and-submit).
export function upgradeCompanyDraft(value: unknown) {
  if (!value || typeof value !== "object" || !("step" in value) || !Number.isInteger(value.step)) return value;
  const stages = !("flowVersion" in value) ? [0, 0, 1, 1, 2] : value.flowVersion === 2 ? [0, 0, 0, 1, 1, 2] : null;
  const priorStep = value.step as number;
  const current = stages && priorStep >= 0 && priorStep < stages.length ? { ...value, flowVersion: COMPANY_FLOW_VERSION, step: stages[priorStep] }
    : "flowVersion" in value && value.flowVersion === 3 ? { ...value, flowVersion: COMPANY_FLOW_VERSION } : value;
  const priorForm = "form" in value && value.form && typeof value.form === "object" ? value.form : {};
  const enrichmentFields = ["tagline", "ownershipClassifications", "corporateRelationship", "enterpriseClassifications", "operatingStatus", "profileRepresents", "secondaryIndustries", "productsServices", "customerMarkets", "serviceCoverage"];
  const hasEnrichment = enrichmentFields.some(field => {
    const entry: unknown = field in priorForm ? Reflect.get(priorForm, field) : undefined;
    return Array.isArray(entry) ? entry.length > 0 : typeof entry === "string" && entry.length > 0;
  });
  const pendingEnrichment = "pendingTokens" in value && value.pendingTokens && typeof value.pendingTokens === "object"
    && Object.values(value.pendingTokens).some(entry => typeof entry === "string" && entry.length > 0);
  return { coverUploaded: false, coverRemoved: false, existingCoverUrl: "", proofType: "", proofSubmissionId: "", legacyExtras: Boolean(hasEnrichment || pendingEnrichment || ("existingCoverUrl" in value && value.existingCoverUrl) || ("coverRemoved" in value && value.coverRemoved)), ...current };
}

export interface StepProps { tax: TaxonomyPayload; form: Form; issue?: ProfileIssue | null; set: <K extends keyof Form>(k: K, v: Form[K]) => void; }

export type PendingTokens = { secondaryIndustries: string; productsServices: string; customerMarkets: string };
